import { lazy, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAlertStore from '../../stores/alertStore';
import LoadingAnim from '../../assets/anims/loading-anim.json';
import Lottie from 'lottie-react';
import { fetchTenantRegistration, finishTenantRegistration } from '../../services/tenantRegistrationService';
import Topbar from './components/topbar/topbar';
import { ILease } from '../../services/leaseService';
import { PaymentPurpose } from '../../services/paymentService';
import useCustomNavigate from '../../hooks/useCustomNavigate';

// Lazy load components
const ReviewContractDetails = lazy(() => import('./sub-pages/review-contract-details/review-contract-details'));
const ReviewDocuments = lazy(() => import('./sub-pages/review-documents/review-documents'));
const Finished = lazy(() => import('./sub-pages/finished/finished'));

const TenantRegistrationPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useCustomNavigate();

    // Stores
    const showAlert = useAlertStore(state => state.showAlert);

    // States
    const [tenancyContract, setTenancyContract] = useState<ILease | null>(null);
    const [isInitialLoaded, setIsInitialLoaded] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const token = searchParams.get('token');

        if (!token) {
            showAlert("error", "No authentication token provided in URL");
            navigate("/home");
            return;
        }

        const loadContractDetails = async () => {
            try {
                setIsLoading(true);
                const tenancyContract = await fetchTenantRegistration(token);
                setTenancyContract(tenancyContract);
                setCurrentStep(tenancyContract.occupants.length > 0 ? 1 : 0);
                setIsInitialLoaded(true);
            } catch (error) {
                console.log(`Error while fetching tenancy contract:`, error);
                // @ts-ignore
                showAlert("error", error.message);
            } finally {
                setIsLoading(false);
            }
        }
        loadContractDetails();
    }, [searchParams, showAlert]);

    const onClickFinish = async () => {
        const token = searchParams.get('token');

        if (!token) {
            showAlert("error", "No authentication token provided in URL");
            return;
        }

        try {
            setIsLoading(true);

            await finishTenantRegistration(token);
            setCurrentStep(2);
        } catch (error) {
            console.log(`Error while finishing tenant registration:`, error);
            // @ts-ignore
            showAlert("error", error.message);
        } finally {
            setIsLoading(false);
        }
    }

    if (!tenancyContract) return null;

    return (
        <div>
            <Topbar currentStep={currentStep} />

            {isInitialLoaded && !isLoading ? (
                <>
                    {currentStep === 0 && (
                        <ReviewContractDetails
                            token={searchParams.get('token') || ""}
                            tenancyContract={tenancyContract}
                            onClickNext={(updatedLease) => {
                                setTenancyContract(updatedLease);
                                setCurrentStep(1);
                            }}
                        />
                    )}
                    {currentStep === 1 && (
                        <ReviewDocuments
                            isLoading={isLoading}
                            token={searchParams.get('token') || ""}
                            tenancyContract={tenancyContract}
                            onClickNext={onClickFinish}
                        />
                    )}
                    {currentStep === 2 && (
                        <Finished />
                    )}
                </>
            ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', marginTop: 100 }}>
                    <Lottie animationData={LoadingAnim} loop={true} style={{ height: 100 }} />
                </div>
            )}
        </div>
    );
};

export default TenantRegistrationPage;
